// import node module libraries
import React, { use, useEffect, useState } from 'react';
import { Col, Button, Form, Row, Card, Dropdown, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

// Import authentication components
import useAuthHeader from 'react-auth-kit/hooks/useAuthHeader';
import useAuthUser from 'react-auth-kit/hooks/useAuthUser';

// import custom components
import { FlatPickr } from 'components/elements/flat-pickr/FlatPickr';
import ApexCharts from 'components/elements/charts/ApexCharts';
import StatRightIcon from 'components/dashboard/common/stats/StatRightIcon';

// import notification component
import Notification, { handleShowNotification } from 'components/elements/notification/Notification';

// The forwardRef is important!!
// Dropdown needs access to the DOM node in order to position the Menu
const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
	<Link
		to=""
		ref={ref}
		onClick={(e) => {
			e.preventDefault();
			onClick(e);
		}}
		className="btn-icon btn btn-ghost btn-sm rounded-circle"
	>
		{children}
	</Link>
));

const ChartActionMenu = () => {
	return (
		<div>
			<Dropdown>
				<Dropdown.Toggle as={CustomToggle}>
					<i className="fe fe-more-vertical text-muted"></i>
				</Dropdown.Toggle>
				<Dropdown.Menu align="end">
					<Dropdown.Header>{t('SETTINGS')}</Dropdown.Header>
					<Dropdown.Item eventKey="1">
						<i className="fe fe-external-link dropdown-item-icon "></i> {t('Export')}
					</Dropdown.Item>
					<Dropdown.Item eventKey="2">
						<i className="fe fe-mail dropdown-item-icon "></i> {t('Email Report')}
					</Dropdown.Item>
					<Dropdown.Item eventKey="3">
						<i className="fe fe-download dropdown-item-icon "></i> {t('Download')}
					</Dropdown.Item>
				</Dropdown.Menu>
			</Dropdown>
		</div>
	);
};

const Overview = () => {
	const { t } = useTranslation(); // useTranslation-Hook
	const [notification, setNotification] = useState({ variant: '', title: '', message: '' });
	const [dataStatisticsSummary, setDataStatisticsSummary] = useState(null); // Add data state
	const [dataStatisticsFlows, setDataStatisticsFlows] = useState(null); // Add data state
	const [highestFlowValue, setHighestFlowValue] = useState(0); // State for highest flow value
	const [isLoading, setIsLoading] = useState(true);
	const [isFailed, setIsFailed] = useState(null); // State for show errors
	const [isCheckboxDemoMode, setIsCheckboxDemoMode] = useState(false); // State checkbox Info Invoice

	// Checkbox Demo Mode
	const onChangeDemoMode = (e) => {
		const isChecked = e.target.checked;
		setIsCheckboxDemoMode(isChecked);
		if (isChecked === true) {
			setDataStatisticsSummary({
				flows: {
					per_day: {
						this_day: 350,
						last_day: 354,
						diff: -4,
						trend: 'down'
					},
					per_month: {
						this_month: 11569,
						last_month: 12005,
						diff: 436,
						trend: 'up'
					},
					per_year: {
						this_year: 122224,
						last_year: 24268,
						diff: 4300,
						trend: 'up'
					},
					with_errors: {
						this_day: 250,
						trend: 'down'
					}
				}
			});
			setDataStatisticsFlows({
				flows: {
					each_flow: {
						order: {
							label: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
							values: {
								name: 'order',
								data: [3200, 4500, 3123, 5345, 2587, 1721, 5910, 2150, 6321, 2567, 1789, 3932]
							}
						},
						return: {
							label: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
							values: {
								name: 'return',
								data: [1523, 2678, 1432, 2356, 2278, 1190, 2012, 987, 1834, 1765, 1654, 1543]
							}
						},
						exchange: {
							label: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
							values: {
								name: 'exchange',
								data: [812, 1865, 923, 1978, 1034, 2098, 1156, 2210, 1267, 2323, 1389, 1445]
							}
						},
						refund: {
							label: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
							values: {
								name: 'refund',
								data: [623, 1678, 734, 1789, 1845, 901, 1956, 1012, 2078, 1134, 1190, 2245]
							}
						},
						repair: {
							label: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
							values: {
								name: 'repair',
								data: [312, 1334, 356, 1378, 1401, 423, 1445, 467, 1489, 512, 1534, 556]
							}
						},
						other: {
							label: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
							values: {
								name: 'other',
								data: [123, 1145, 167, 1189, 1210, 234, 1256, 278, 1300, 1321, 1345, 1367]
							}
						}
					},
					distribution: {
						label: ['order', 'return', 'exchange', 'refund', 'repair', 'other'],
						values: [40, 25, 10, 15, 5, 5]
					}
				}
			}),
			setHighestFlowValue({ key: 'order', value: 7000 });
		} else {
			getData();
		}
	};
	
	const auth = useAuthUser(); // Assuming this gets the auth user data

	// API-Call Options
	const authHeader = useAuthHeader();
	const options = {
		params: {

		},
		headers: {
			Authorization: authHeader,
			Accept: 'application/json'
		}
	};

	// API-Call for all Data
	const getData = async () => {
		setIsLoading(true);

		const thisDay = new Date().toISOString().split('T')[0]; // Today's date
		const lastDay = new Date(Date.now() - 86400000).toISOString().split('T')[0]; // Yesterday's date
		const firstDayOfThisMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]; // First day of this month
		const lastDayOfThisMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0]; // Last day of this month
		const firstDayOflastMonth = new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1).toISOString().split('T')[0]; // First day of last month
		const lastDayOfLastMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 0).toISOString().split('T')[0]; // Last day of last month
		const firstDayOfThisYear = new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0]; // First day of this year
		const lastDayOfThisYear = new Date(new Date().getFullYear(), 11, 31).toISOString().split('T')[0]; // Last day of this year
		const firstDayOfLastYear = new Date(new Date().getFullYear() - 1, 0, 1).toISOString().split('T')[0]; // First day of last year
		const lastDayOfLastYear = new Date(new Date().getFullYear() - 1, 11, 31).toISOString().split('T')[0]; // Last day of last year
		const last11Months = new Date(new Date().setMonth(new Date().getMonth() - 11)).toISOString().split('T')[0]; // Last 11 months from today

		// HIER WEITER MACHEN
		const periods = ['day', 'day', 'month', 'month', 'year', 'year', 'year']; // Adjust these values as needed
		const periodStartValues = [
			thisDay, 
			lastDay, 
			firstDayOfThisMonth,
			firstDayOflastMonth, 
			firstDayOfThisYear, 
			firstDayOfLastYear,
			last11Months
		];
		const periodEndValues = [
			thisDay, 
			lastDay,
			lastDayOfThisMonth,
			lastDayOfLastMonth,
			lastDayOfThisYear,
			lastDayOfLastYear,
			thisDay
		];

		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		const requests = periods.map((period, index) =>
			axios.get(`${baseURL}/stats?period=${period}&period_start=${periodStartValues[index]}&period_end=${periodEndValues[index]}&tenant_id=${auth?.tenant_id}`, options)
		);
		
		try {
			const responses = await Promise.all(requests);
			if (responses.some(response => !response.data.data)) {
				return;
			}
			const flows = responses.map(response => {
				const data = response.data.data.return;
				return Array.isArray(data) ? data.reduce((acc, curr) => acc + curr.value, 0) : 0;
			});
			
			setDataStatisticsSummary({
				flows: {
					per_day: {
						this_day: flows[0],
						last_day: flows[1],
						diff: flows[0] - flows[1],
						trend: flows[0] - flows[1] < 0 ? 'down' : 'up'
					},
					per_month: {
						this_month: flows[2],
						last_month: flows[3],
						diff: flows[2] - flows[3],
						trend: flows[2] - flows[3] < 0 ? 'down' : 'up'
					},
					per_year: {
						this_year: flows[4],
						last_year: flows[5],
						diff: flows[4] - flows[5],
						trend: flows[4] - flows[5] < 0 ? 'down' : 'up'
					},
					with_errors: {
						this_year: 0,
						trend: 'up'
					}
				}
			});
			
			const last11MonthsData = responses[6].data.data;

			const eachFlow = Object.keys(last11MonthsData).reduce((acc, key) => {
				const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
				const currentMonth = new Date().getMonth();
				const sortedMonthNames = [...monthNames.slice(currentMonth + 1), ...monthNames.slice(0, currentMonth + 1)];
				
				acc[key] = { label: [], values: { data: [], name: key } };
				last11MonthsData[key].forEach(item => {
					if (item.month && item.month >= 1 && item.month <= 12) {
						acc[key].label.push(monthNames[item.month - 1]);
						acc[key].values.data.push(item.value);
					}
				});
				
				// Sort the data based on the sorted month names
				const sortedData = sortedMonthNames.map(month => {
					const index = acc[key].label.indexOf(month);
					return index !== -1 ? acc[key].values.data[index] : 0;
				});
				
				acc[key].label = sortedMonthNames;
				acc[key].values.data = sortedData;
				
				return acc;
			}, {});

			// Sort eachFlow by the total value in descending order
			const sortedEachFlow = Object.entries(eachFlow)
				.sort(([, a], [, b]) => b.values.data.reduce((sum, val) => sum + val, 0) - a.values.data.reduce((sum, val) => sum + val, 0))
				.reduce((acc, [key, value]) => {
					acc[key] = value;
					return acc;
				}, {});
			
			const highestFlowValue = Object.entries(sortedEachFlow).reduce((acc, [key, value]) => {
				const max = Math.max(...value.values.data);
				return max > acc.value ? { key, value: max } : acc;
			}, { key: '', value: 0 });

			highestFlowValue.value = Math.ceil(highestFlowValue.value / 1000) * 1000;

			setHighestFlowValue(highestFlowValue);

			const totalValues = Object.values(last11MonthsData).reduce((acc, curr) => {
				return acc + curr.reduce((sum, item) => sum + item.value, 0);
			}, 0);

			const distribution = {
				label: [],
				values: []
			};

			const distributionData = Object.keys(last11MonthsData).map(key => {
				const keyTotal = last11MonthsData[key].reduce((sum, item) => sum + item.value, 0);
				const percentage = Math.round((keyTotal / totalValues) * 100);
				return {
					label: key,
					value: percentage < 1 ? 1 : (percentage > 99 && percentage !== 100) ? 99 : percentage
				};
			});

			distributionData.sort((a, b) => b.value - a.value);

			distribution.label = distributionData.map(item => item.label);
			distribution.values = distributionData.map(item => item.value);
			
			setDataStatisticsFlows({
				flows: {
					each_flow: sortedEachFlow,
					distribution: distribution
				}
			});

			setIsLoading(false);
			setIsFailed(null);
		} catch (error) {
			setIsLoading(false);
			const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
			handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Bitte versuche es erneut oder kontaktiere den HelpDesk.');	
		}
		
		
	};

	const theme = {
		flow1color: 'var(--geeks-primary)',
		flow2color: 'var(--geeks-secondary)',
		flow3color: 'var(--geeks-success)',
		flow4color: 'var(--geeks-warning)',
		flow5color: 'var(--geeks-danger)',
		flow6color: 'var(--geeks-black)',
		primary: 'var(--geeks-primary)',
		secondary: 'var(--geeks-secondary)',
		success: 'var(--geeks-success)',
		info: 'var(--geeks-info)',
		warning: 'var(--geeks-warning)',
		danger: 'var(--geeks-danger)',
		dark: 'var(--geeks-dark)',
		light: 'var(--geeks-light)',
		white: 'var(--geeks-white)',
		gray100: 'var(--geeks-gray-100)',
		gray200: 'var(--geeks-gray-200)',
		gray300: 'var(--geeks-gray-300)',
		gray400: 'var(--geeks-gray-400)',
		gray500: 'var(--geeks-gray-500)',
		gray600: 'var(--geeks-gray-600)',
		gray700: 'var(--geeks-gray-700)',
		gray800: 'var(--geeks-gray-800)',
		gray900: 'var(--geeks-gray-900)',
		black: 'var(--geeks-black)',
		lightPrimary: 'var(--geeks-light-primary)',
		lightSecondary: 'var(--geeks-light-secondary)',
		lightSuccess: 'var(--geeks-light-success)',
		lightDanger: 'var(--geeks-light-danger)',
		lightWarning: 'var(--geeks-light-warning)',
		lightInfo: 'var(--geeks-light-info)',
		darkPrimary: 'var(--geeks-dark-primary)',
		darkSecondary: 'var(--geeks-dark-secondary)',
		darkSuccess: 'var(--geeks-dark-success)',
		darkDanger: 'var(--geeks-dark-danger)',
		darkWarning: 'var(--geeks-dark-warning)',
		darkInfo: 'var(--geeks-dark-info)',
		transparent: 'transparent',
		borderColor: 'var(--geeks-border-color)'
	};

	const ChartOptionsFlow12Months = {
		height: 'auto',
		labels: [...new Set(Object.values(dataStatisticsFlows?.flows?.each_flow || {}).flatMap(flow => flow.label))].map(label => t(label)),
		chart: {
			fontFamily: '$font-family-base',
			height: '30px',
			type: 'line',
			toolbar: { show: !1 }
		},
		stroke: { width: 4, curve: 'smooth' },
		colors: [theme.flow1color, theme.flow2color, theme.flow3color, theme.flow4color, theme.flow5color, theme.flow6color],
		xaxis: {
			axisBorder: { show: !1 },
			axisTicks: { show: !1 },
			crosshairs: { show: !0 },
			labels: {
				style: {
					fontSize: '13px',
					fontWeight: 400,
					colors: theme.gray500,
					fontFamily: 'inter'
				}
			}
		},
		yaxis: {
			labels: {
				formatter: function (e) {
					return e;
				},
				style: { fontSize: '13px', fontWeight: 400, colors: theme.gray500 },
				offsetX: -15
			},
			tickAmount: 5,
			min: 0,
			max: highestFlowValue.value
		},
		grid: {
			borderColor: theme.borderColor,
			strokeDashArray: 5,
			xaxis: { lines: { show: !1 } },
			yaxis: { lines: { show: !0 } },
			padding: { top: 0, right: 0, bottom: 0, left: 0 }
		},
		legend: {
			position: 'top',
			horizontalAlign: 'right',
			offsetY: -50,
			fontSize: '16px',
			markers: {
				width: 10,
				height: 10,
				strokeWidth: 0,
				strokeColor: theme.white,
				fillColors: void 0,
				radius: 12,
				onClick: void 0,
				offsetX: 0,
				offsetY: 0
			},
			itemMargin: { horizontal: 0, vertical: 20 }
		},
		tooltip: {
			y: [
				{
					title: {
						formatter: function (e) {
							return t(e)+':';
						}
					}
				},
				{
					title: {
						formatter: function (e) {
							return t(e) + ':';
						}
					}
				},
				{
					title: {
						formatter: function (e) {
							return t(e) + ':';
						}
					}
				},
				{
					title: {
						formatter: function (e) {
							return t(e) + ':';
						}
					}
				},
				{
					title: {
						formatter: function (e) {
							return t(e) + ':';
						}
					}
				},
				{
					title: {
						formatter: function (e) {
							return t(e) + ':';
						}
					}
				}
			]
		},
		responsive: [
			{
				breakpoint: 480,
				options: {
					chart: {
						height: 300
					}
				}
			},
			{
				breakpoint: 5000,
				options: {
					chart: {
						height: 280
					}
				}
			}
		]
	};

	const ChartOptionsFlowDistribution = {
		dataLabels: { enabled: !1 },
		labels: Object.values(dataStatisticsFlows?.flows?.distribution?.label || []).map(label => t(label)),
		colors: [theme.flow1color, theme.flow2color, theme.flow3color, theme.flow4color, theme.flow5color, theme.flow6color],
		legend: {
			labels: { colors: [theme.gray500], useSeriesColors: !1 },
			position: 'bottom'
		},
		plotOptions: { pie: { expandOnClick: !1, donut: { size: '70%' } } },
		chart: {
			type: 'donut', 
			events: {
				beforeMount: function () {
					// Passive event listeners for better performance
				}
			},
		},
		tooltip: { theme: 'light', marker: { show: !0 }, x: { show: !1 } },
		states: { hover: { filter: { type: 'none' } } },
		stroke: { show: !0, colors: [theme.transparent] },
		responsive: [
			{
				breakpoint: 480,
				options: {
					chart: {
						height: 300
					}
				}
			},
			{
				breakpoint: 5000,
				options: {
					chart: {
						height: 280
					}
				}
			}
		]
	};
	
	useEffect(() => {
		getData();
	}, []);

	return (
		<div>
			{notification.show && (
				<Notification variant={notification.variant} title={notification.title} message={notification.message} show={notification.show} setNotification={setNotification} />
			)}
			<Row>
				<Col lg={12} md={12} sm={12}>
					<div className="border-bottom pb-4 mb-4 d-lg-flex justify-content-between align-items-center">
						<div className="mb-3 mb-lg-0">
							<h1 className="mb-0 h2 fw-bold">{t('Overview')}</h1>
						</div>
						<div className="d-flex align-items-center">
							<Form>
								<Form.Check
									type="checkbox"
									label=""
									className="form-switch"
									onChange={onChangeDemoMode}
									checked={isCheckboxDemoMode}
								/>
							</Form><span className="">{t('Demo mode')}</span>
						</div>
					</div>
				</Col>
			</Row>
			<Row>
				<Col xl={3} lg={6} md={12} sm={12}>
					<StatRightIcon
						title={t('FLOWS THIS YEAR')}
						value={dataStatisticsSummary?.flows?.per_year?.this_year.toLocaleString('de-DE')}
						summary={t('to last year')}
						summaryValue={dataStatisticsSummary?.flows?.per_year?.diff.toLocaleString('de-DE')}
						summaryIcon={dataStatisticsSummary?.flows?.per_year?.trend}
						showSummaryIcon
						iconName="git-pull-request"
						iconColorVariant="primary"
						classValue="mb-4"
					/>
				</Col>

				<Col xl={3} lg={6} md={12} sm={12}>
					<StatRightIcon
						title={t('FLOWS THIS MONTH')}
						value={dataStatisticsSummary?.flows?.per_month?.this_month.toLocaleString('de-DE')}
						summary={t('to last month')}
						summaryValue={dataStatisticsSummary?.flows?.per_month?.diff.toLocaleString('de-DE')}
						summaryIcon={dataStatisticsSummary?.flows?.per_month?.trend}
						showSummaryIcon
						iconName="git-pull-request"
						iconColorVariant="primary"
						classValue="mb-4"
					/>
				</Col>

				<Col xl={3} lg={6} md={12} sm={12}>
					<StatRightIcon
						title={t('FLOWS TODAY')}
						value={dataStatisticsSummary?.flows?.per_day?.this_day.toLocaleString('de-DE')}
						summary={t('to yesterday')}
						summaryValue={dataStatisticsSummary?.flows?.per_day?.diff.toLocaleString('de-DE')}
						summaryIcon={dataStatisticsSummary?.flows?.per_day?.trend}
						showSummaryIcon
						iconName="git-pull-request"
						iconColorVariant="primary"
						classValue="mb-4"
					/>
				</Col>

				<Col xl={3} lg={6} md={12} sm={12}>
					<StatRightIcon
						title={t('FLOWS WITH ERRORS')}
						value={dataStatisticsSummary?.flows?.with_errors?.this_day?.toLocaleString('de-DE') || 0}
						summary={t('errors occurred')}
						summaryValue={dataStatisticsSummary?.flows?.with_errors?.this_day?.toLocaleString('de-DE') || 'Keine'}
						summaryIcon={dataStatisticsSummary?.flows?.with_errors?.trend}
						iconName="git-pull-request"
						iconColorVariant="primary"
						classValue="mb-4"
					/>
				</Col>
			</Row>

			<Row>
				<Col xl={8} lg={12} md={12} className="mb-4">
					<Card>
						<Card.Header className="align-items-center card-header-height d-flex justify-content-between align-items-center">
							<div>
								<h4 className="mb-0">{t('All flows')}</h4>
							</div>
						</Card.Header>
						<Card.Body>
							<ApexCharts
								key={`line-chart-${Object.keys(dataStatisticsFlows?.flows?.each_flow || {}).length}`}
								options={ChartOptionsFlow12Months}
								series={Object.values(dataStatisticsFlows?.flows?.each_flow || {}).map(flow => flow.values)}
								type="line"
							/>
						</Card.Body>
					</Card>
				</Col>
				<Col xl={4} lg={12} md={12} className="mb-4">
					<Card>
						<Card.Header className="align-items-center card-header-height d-flex justify-content-between align-items-center">
							<div>
								<h4 className="mb-0">{t('Procentual distribution')}</h4>
							</div>
						</Card.Header>
						<Card.Body className="py-lg-7">
							<div id="chart">
								<ApexCharts
									key={`donut-chart-${dataStatisticsFlows?.flows?.distribution?.values?.length || 0}`}
									options={ChartOptionsFlowDistribution}
									series={Object.values(dataStatisticsFlows?.flows?.distribution.values || [])}
									type="donut"
								/>
							</div>
						</Card.Body>
					</Card>
				</Col>
			</Row>
		</div>
	);
};

export default Overview;
